import * as Sentry from '@sentry/react';

/**
 * Determines the current environment based on Cloudflare Pages variables
 *
 * @returns 'production' if running on Cloudflare Pages main branch,
 *          'preview' if running on Cloudflare Pages feature branch,
 *          'development' if running locally
 */
function getEnvironment(): 'production' | 'preview' | 'development' {
  const isCFPages = import.meta.env.VITE_CF_PAGES === '1';
  const cfBranch = import.meta.env.VITE_CF_PAGES_BRANCH;

  if (isCFPages) {
    // Running on Cloudflare Pages
    if (cfBranch === 'main') {
      return 'production';
    } else {
      return 'preview';
    }
  }

  // Running locally
  return import.meta.env.MODE === 'production' ? 'production' : 'development';
}

/**
 * Initializes Sentry error tracking with intelligent environment detection
 *
 * This function automatically:
 * - Detects whether running on Cloudflare Pages (production/preview) or locally (development)
 * - Uses git commit SHA from Cloudflare Pages as the release ID
 * - Configures appropriate sampling rates for each environment
 * - Enables session replay and performance monitoring
 *
 * Configuration is controlled via environment variables:
 * - VITE_SENTRY_DSN: Sentry Data Source Name (required)
 * - VITE_SENTRY_ENABLED: Set to 'false' to disable Sentry (default: true)
 *
 * Cloudflare Pages automatically provides:
 * - VITE_CF_PAGES: '1' when running on Cloudflare Pages
 * - VITE_CF_PAGES_BRANCH: Git branch name (e.g., 'main', 'feature/xyz')
 * - VITE_CF_PAGES_COMMIT_SHA: Git commit hash
 * - VITE_CF_PAGES_URL: Deployment URL
 */
export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const enabled = import.meta.env.VITE_SENTRY_ENABLED !== 'false';
  const environment = getEnvironment();

  // Skip initialization if DSN not provided or Sentry is disabled
  if (!dsn || !enabled) {
    if (environment === 'development') {
      console.log('[Sentry] Skipped initialization (DSN not provided or disabled)');
    }
    return;
  }

  Sentry.init({
    dsn,
    environment,

    // Use git commit SHA from Cloudflare Pages as release ID
    // This enables tracking which code version caused errors
    release: import.meta.env.VITE_CF_PAGES_COMMIT_SHA || undefined,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(), // Performance monitoring
      Sentry.replayIntegration(),         // Session replay for debugging
      Sentry.consoleLoggingIntegration({  // Capture console logs
        levels: ['log', 'warn', 'error']
      }),
    ],

    // Performance Monitoring - capture 100% of transactions
    tracesSampleRate: 1.0,

    // Distributed tracing - enable for localhost and production domain
    tracePropagationTargets: ['localhost', /^https:\/\/noctos.io/],

    // Session Replay - sample rates vary by environment
    replaysSessionSampleRate: environment === 'development' ? 1.0 : 0.1, // 100% dev, 10% prod
    replaysOnErrorSampleRate: 1.0, // Always capture sessions with errors

    // Send personally identifiable information (PII)
    // This includes IP addresses and user agent strings
    sendDefaultPii: true,

    // Enable logs to be sent to Sentry
    beforeSend(event) {
      // Log to console in development
      if (environment === 'development') {
        console.log('[Sentry] Capturing event:', event);
      }
      return event;
    },
  });

  if (environment === 'development') {
    console.log('[Sentry] Initialized:', {
      environment,
      release: import.meta.env.VITE_CF_PAGES_COMMIT_SHA || 'local-dev',
      dsn: dsn.substring(0, 20) + '...',
    });
  }
}
